import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCheckDbForUser, apiValidatePassword } from './loginApi';
import { getApiUrl } from './config';
import { validateAndCleanToken, clearAuthData, scheduleTokenRefresh, getDefaultRoute, getCurrentUser } from './authUtils';
import { usePageTitle } from './hooks/usePageTitle';

// UI States
const LoginStep = {
  Username: 0,
  Password: 1,
  Success: 2,
  Failure: 3,
  Loading: 4,
};

function LoginPortal() {
  usePageTitle('Login');
  const navigate = useNavigate();
  const [step, setStep] = useState(LoginStep.Username);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Clear any expired or invalid tokens on mount to ensure clean state
  useEffect(() => {
    // If there's a valid token, redirect to their default route
    if (validateAndCleanToken()) {
      const user = getCurrentUser();
      navigate(getDefaultRoute(user));
    }
    // If validateAndCleanToken returns false, it already cleared any invalid tokens
  }, [navigate]);

  // API stubs (replace with real endpoints)

  async function checkDbForUser(user) {
    setLoading(true);
    setError('');
    const result = await apiCheckDbForUser(user);
    setLoading(false);
    return result;
  }

  async function validatePassword(user, pwd) {
    setLoading(true);
    setError('');
    const result = await apiValidatePassword(user, pwd);
    setLoading(false);
    return result;
  }

  async function handleUsernameSubmit(e) {
    e.preventDefault();
    if (!username) return setError('Username required');
    const result = await checkDbForUser(username);
    if (result.error) {
      setError('Invalid user');
      setStep(LoginStep.Failure);
      return;
    }
    if (result.sso) {
      // Redirect to organization-specific SSO
      const ssoUrl = result.sso_redirect_url;
      if (!ssoUrl) {
        setError('SSO configuration error. Please contact support.');
        return;
      }
      window.location.href = getApiUrl(ssoUrl);
    } else {
      // No SSO or user/org not found - prompt for password
      // This prevents username harvesting by not disclosing if user exists
      setStep(LoginStep.Password);
    }
  }

  async function handlePasswordSubmit(e) {
    e.preventDefault();
    if (!password) return setError('Password required');
    const result = await validatePassword(username, password);
    if (result.success) {
      // Clear any existing auth data before storing new token
      clearAuthData();
      // Store JWT securely in localStorage
      localStorage.setItem('auth_token', result.jwt);
      localStorage.setItem('user_email', username);
      scheduleTokenRefresh();
      setStep(LoginStep.Success);
      // Navigate to their default route (same as SSO flow)
      setTimeout(() => {
        const user = getCurrentUser();
        navigate(getDefaultRoute(user));
      }, 1500);
    } else {
      setError('Invalid password');
      setStep(LoginStep.Failure);
    }
  }

  function handleRetry() {
    setUsername('');
    setPassword('');
    setError('');
    setStep(LoginStep.Username);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
      <div className="bg-gray-950 rounded-2xl shadow-2xl p-10 w-80 flex flex-col gap-6 text-gray-50 animate-fade-in">
        {step === LoginStep.Username && (
          <div>
            <form onSubmit={handleUsernameSubmit} className="flex flex-col gap-3">
              <h2 className="text-xl font-semibold tracking-tight mb-2">Sign in to SmartLight Analytics</h2>
              <input
                type="text"
                placeholder="Email address"
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoFocus
                className="w-full px-4 py-2 rounded-lg border border-gray-700 bg-gray-900 text-gray-50 text-base outline-none focus:border-blue-500 transition-colors box-border"
              />
              {error && (
                <div className="text-red-400 bg-red-950 px-3 py-2 rounded-lg text-sm">{error}</div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold text-base cursor-pointer mt-1 disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Checking...' : 'Next'}
              </button>
            </form>
          </div>
        )}
        {step === LoginStep.Password && (
          <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-3">
            <h2 className="text-xl font-semibold tracking-tight mb-2">Enter your password</h2>
            <div className="text-gray-400 text-sm mb-2 px-2 py-2 bg-gray-900 rounded-lg text-center">{username}</div>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus
              className="w-full px-4 py-2 rounded-lg border border-gray-700 bg-gray-900 text-gray-50 text-base outline-none focus:border-blue-500 transition-colors box-border"
            />
            {error && (
              <div className="text-red-400 bg-red-950 px-3 py-2 rounded-lg text-sm">{error}</div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold text-base cursor-pointer mt-1 disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Validating...' : 'Sign in'}
            </button>
            <button
              type="button"
              onClick={() => { setStep(LoginStep.Username); setPassword(''); setError(''); }}
              className="w-full py-2 rounded-lg bg-transparent text-blue-400 border border-gray-700 font-semibold text-base cursor-pointer mt-1 hover:bg-gray-900 hover:border-blue-500 transition-colors"
            >
              Back
            </button>
          </form>
        )}
        {step === LoginStep.Failure && (
          <div className="flex flex-col gap-3">
            <h2 className="text-xl font-semibold tracking-tight mb-2">Login Failed</h2>
            <div className="text-red-400 bg-red-950 px-3 py-2 rounded-lg text-sm">{error || 'Login failed. Please try again.'}</div>
            <button
              onClick={handleRetry}
              className="w-full py-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold text-base cursor-pointer mt-1 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}
        {step === LoginStep.Success && (
          <div className="flex flex-col gap-3">
            <h2 className="text-xl font-semibold tracking-tight mb-2">Welcome!</h2>
            <div className="text-green-400 bg-green-950 px-3 py-2 rounded-lg text-sm">You are now signed in.</div>
            <div className="text-gray-400 text-sm text-center">Redirecting...</div>
          </div>
        )}
      </div>
    </div>
  );
}

export { LoginPortal };
